"use client"

import * as React from "react"
import type { Column } from "@tanstack/react-table"
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface DataTableSortHeaderProps<TData, TValue>
  extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>
  label: React.ReactNode
  /**
   * Right-align labels for numeric columns so the digits live next to the
   * sort indicator (matches the body cell which is typically right-aligned).
   */
  align?: "left" | "right"
}

/**
 * Shared sortable header used by every UnifiedDataTable column. Renders a
 * label + a 3-state sort indicator (asc / desc / unset). Falls back to a
 * plain bold label when the column has `enableSorting: false`, so columns
 * like "Status" or "Actions" never show a misleading sort affordance.
 *
 * Locked 2026-05-06 to replace per-column copies of `<ArrowUpDown … />`
 * scattered across phishing/recipients/lms/portal columns. Keeps icon size
 * + spacing identical everywhere so the right-edge sort icon never clips.
 */
export function DataTableSortHeader<TData, TValue>({
  column,
  label,
  align = "left",
  className,
  ...props
}: DataTableSortHeaderProps<TData, TValue>) {
  if (!column.getCanSort()) {
    return (
      <div
        className={cn(
          "font-bold",
          align === "right" && "text-right",
          className,
        )}
        {...props}
      >
        {label}
      </div>
    )
  }

  const sorted = column.getIsSorted()
  const Icon =
    sorted === "asc" ? ArrowUp : sorted === "desc" ? ArrowDown : ChevronsUpDown

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => column.toggleSorting(sorted === "asc")}
      className={cn(
        "-ml-3 h-8 px-2 font-bold data-[state=on]:bg-transparent",
        align === "right" && "ml-auto -mr-2",
        className,
      )}
      {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      <span className="truncate">{label}</span>
      <Icon
        className={cn(
          "ml-1 h-3.5 w-3.5 shrink-0",
          sorted ? "text-foreground" : "text-muted-foreground/60",
        )}
      />
    </Button>
  )
}
